import AppContext from "@common/contexts/App";
import ResponsiveContainer from "@common/controls/ResponsiveContainer/component";
import { useI18n } from "@common/hooks/i18n";
import { Common } from "@common/types";
import loadable from "@loadable/component";
import { type ActiveElement, type ChartData, type ChartEvent, Chart as ChartJs, type ChartOptions } from "chart.js";
import React, { useCallback } from "react";
import {
  getBubbleData,
  getChartOptions,
  getData,
  getDisplayValue,
  getEmptyConfig,
  getScatterData,
  getScatterOptions
} from "./lib";
import { type BubbleScatterChartConfig, type ChartBaseConfig, type ChartConfig, type ChartType } from "./types";

const ChartJsLoadable = loadable.lib<any, any>(() => import(/* webpackChunkName: "chart.js" */ "chart.js"));

type ChartBaseProps = {
  config: ChartBaseConfig;
  getData: () => ChartData;
  getOptions: (chartjs: any) => ChartOptions;
  type: ChartType;
  data: object[];

  onDrillDown?: (value: any, label: string) => void;
};
const ChartBase = (props: ChartBaseProps & { chartjs: any }) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const chartRef = React.useRef<ChartJs | null>(null);
  const chartJsRef = React.createRef();
  const appContext = React.useContext(AppContext);

  // this must be the first hook
  React.useEffect(() => {
    // recreate the chart when its type changes
    chartRef.current = new props.chartjs.Chart(canvasRef.current!.getContext("2d")!, getEmptyConfig(props.type));
    return () => {
      chartRef.current && chartRef.current.destroy();
    };
  }, [props.chartjs, props.type]);

  React.useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.data = props.getData();
    chartRef.current.update();
  }, [props.getData]);

  React.useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.options = props.getOptions(props.chartjs);
    chartRef.current.update();
  }, [props.chartjs, props.getOptions, appContext.theme]); // props.theme is a dependency because we take colors from the calculated styles

  return (
    <>
      <ResponsiveContainer aspectRatio={props.config.aspectRatio} containerClassName="chart_container">
        <canvas ref={canvasRef}></canvas>
      </ResponsiveContainer>

      <ChartJsLoadable ref={chartJsRef} />
    </>
  );
};

export type ChartProps = Omit<ChartBaseProps, "config" | "getOptions" | "getData" | "config"> & {
  config: ChartConfig;
};
const Chart = (props: ChartProps) => {
  const i18n = useI18n();

  const onClick = React.useCallback(
    (event: ChartEvent, elements: ActiveElement[], chart: ChartJs) => {
      if (!props.config.drillDownKey || !props.onDrillDown) return;
      const points = chart.getElementsAtEventForMode(event as any, "nearest", { intersect: true }, true);
      if (points.length) {
        const firstPoint = points[0];

        const drillDownValue = props.data[firstPoint.index][props.config.drillDownKey];
        props.onDrillDown(drillDownValue, getDisplayValue(props.config.xAxis, props.data[firstPoint.index]));
      }
    },
    [props.onDrillDown, props.data, props.config]
  );

  const getOptions = React.useCallback(
    (chartjs) => getChartOptions(chartjs, props.type, props.config, props.data, onClick),
    [onClick]
  );

  const getDataInternal = useCallback(
    () => getData(i18n, props.type, props.config, props.data),
    [i18n, props.type, props.config, props.data]
  );

  return (
    <ChartJsLoadable>
      {(chartjs) => {
        chartjs.Chart.register(...chartjs.registerables);

        return <ChartBase chartjs={chartjs} {...props} getData={getDataInternal} getOptions={getOptions} />;
      }}
    </ChartJsLoadable>
  );
};

export type BubbleChartPoint = Common.Point & {
  r: number;
};
export type BubleChartProps = Omit<ChartBaseProps, "config" | "getOptions" | "getData" | "data" | "type"> & {
  config: BubbleScatterChartConfig;
  data: BubbleChartPoint[];
};
export const BubbleChart = (props: BubleChartProps) => (
  <ChartJsLoadable>
    {(chartjs) => {
      chartjs.Chart.register(...chartjs.registerables);

      return (
        <ChartBase
          chartjs={chartjs}
          {...props}
          getData={() => getBubbleData(props.config, props.data)}
          getOptions={() => getScatterOptions(props.config, props.data)}
          type="Bubble"
        />
      );
    }}
  </ChartJsLoadable>
);

export type ScatterChartProps = Omit<ChartBaseProps, "config" | "getData" | "data" | "type"> & {
  config: BubbleScatterChartConfig;
  data: Common.Point[];
};
export const ScatterChart = (props: ScatterChartProps) => (
  <ChartJsLoadable>
    {(chartjs) => {
      chartjs.Chart.register(...chartjs.registerables);

      return (
        <ChartBase
          chartjs={chartjs}
          {...props}
          getData={() => getScatterData(props.config, props.data)}
          type="Scatter"
        />
      );
    }}
  </ChartJsLoadable>
);

export const preloadChart = () => ChartJsLoadable.preload();

export default Chart;
